import {
  Directive,
  OnInit,
  OnDestroy,
  ElementRef,
  Input,
  EventEmitter,
  Output,
  Renderer2,
  NgZone,
} from '@angular/core';
import { GridComponent, DataBindingDirective } from '@progress/kendo-angular-grid';
import { Subscription } from 'rxjs';
import { DataResult } from '@progress/kendo-data-query';

export interface GridOptionalFeatures {
  calcRows?: { hasHorizontalScroll: boolean };
  addHover?: boolean;
}

@Directive({
  standalone: false,
  selector: '[tiGridOptionalFeatures]',
})
export class GridOptionalFeaturesDirective extends DataBindingDirective implements OnInit, OnDestroy {
  @Input() set gridData(d: DataResult) {
    if (d && d.data && typeof d.total === 'number') {
      this.initData(d);
    }
  }
  @Input() featureOptions: GridOptionalFeatures;
  @Output() rowsCalculate: EventEmitter<number> = new EventEmitter<number>();
  private rows: number;
  private listeners: Function[] = [];
  private currentData: DataResult;
  private selection$: Subscription;

  constructor(
    protected grid: GridComponent,
    private el: ElementRef,
    private renderer: Renderer2,
    private zone: NgZone,
  ) {
    super(grid);
  }

  private initData(d: DataResult) {
    this.currentData = d;
    this.removeHoverStateListener();
    this.grid.data = d;
    this.notifyDataChange();
    this.rebind();
    this.setHoverStateListener();
  }

  private calcGridRowsCount(): number {
    if (!this.featureOptions || !this.featureOptions.calcRows) {
      return;
    }

    if (this.el.nativeElement.offsetHeight) {
      const heightScroll = 17; // standard scroll sizes ~17
      const heightGridHeader = 29;
      const heightGridRow = 33;
      const pagerOffset = 35; // let's reserve some space for the pager
      const containerHeight = this.el.nativeElement.querySelectorAll('.k-grid-aria-root')[0].offsetHeight;
      const headerHeight = this.el.nativeElement.querySelectorAll('.k-grid-header')[0].offsetHeight;

      if (headerHeight > 1) {
        this.rows = this.featureOptions.calcRows.hasHorizontalScroll
          ? Math.floor((containerHeight - headerHeight - heightScroll) / heightGridRow)
          : Math.floor((containerHeight - headerHeight) / heightGridRow);

        this.rowsCalculate.emit(this.rows);
        return this.rows;
      }
      this.rows = this.featureOptions.calcRows.hasHorizontalScroll
        ? Math.floor((containerHeight - heightGridHeader - heightScroll) / heightGridRow)
        : Math.floor((containerHeight - heightGridHeader) / heightGridRow);

      this.rowsCalculate.emit(this.rows);
      return this.rows;
    } else {
      this.rows = 0;
      this.rowsCalculate.emit(0);
      return 0;
    }
  }

  private setHoverStateListener() {
    const hasData = this.currentData && this.currentData.data && this.currentData.data.length;
    const isEnabledHover = hasData && !(!this.featureOptions || !this.featureOptions.addHover);

    !hasData && this.renderer.addClass(this.el.nativeElement, 'no-data');

    if (!isEnabledHover) {
      return;
    }

    setTimeout(() => {
      if (
        this.el.nativeElement.querySelector(`.k-grid-content-locked`) &&
        !this.el.nativeElement.querySelector(`.k-detail-row`)
      ) {
        const gridRows: HTMLElement[] = Array.from(
          this.el.nativeElement.querySelectorAll('.k-grid .k-grid-container tr'),
        );
        gridRows.forEach((row) => {
          let mouseleave, mouseenter;
          this.zone.runOutsideAngular(() => {
            mouseleave = this.renderer.listen(row, 'mouseleave', () => this.onGridRowMouseLeave(row, this.el));
            mouseenter = this.renderer.listen(row, 'mouseenter', () => this.onGridRowMouseEnter(row, this.el));
          });
          this.listeners.push(mouseleave, mouseenter);
        });
      }
    });
  }

  private removeHoverStateListener() {
    this.renderer.removeClass(this.el.nativeElement, 'no-data');
    if (this.listeners.length) {
      this.listeners.forEach((l) => l());
      this.listeners = [];
    }
  }

  private onGridRowMouseEnter(row: HTMLElement, grid: ElementRef) {
    const index = row.getAttribute('data-kendo-grid-item-index');
    if (!row.classList.contains('k-grid-norecords')) {
      if (row.closest('.k-grid-content') || row.closest('.k-grid-content-locked')) {
        let el = grid.nativeElement.querySelector(`.k-grid-content-locked tr[data-kendo-grid-item-index="${index}"]`);
        el && el.classList.add('k-state-hover');

        el = grid.nativeElement.querySelector(`.k-grid-content tr[data-kendo-grid-item-index="${index}"]`);
        el && el.classList.add('k-state-hover');
      }
    }
  }

  private onGridRowMouseLeave(row: HTMLElement, grid: ElementRef) {
    const index = row.getAttribute('data-kendo-grid-item-index');
    if (!row.classList.contains('k-grid-norecords')) {
      if (row.closest('.k-grid-content') || row.closest('.k-grid-content-locked')) {
        let el = grid.nativeElement.querySelector(`.k-grid-content-locked tr[data-kendo-grid-item-index="${index}"]`);
        el && el.classList.remove('k-state-hover');

        el = grid.nativeElement.querySelector(`.k-grid-content tr[data-kendo-grid-item-index="${index}"]`);
        el && el.classList.remove('k-state-hover');
      }
    }
  }

  public ngOnInit(): void {
    this.selection$ = this.grid.selectionChange.subscribe(() => {
      this.removeHoverStateListener();
      this.setHoverStateListener();
    });
    setTimeout(() => this.calcGridRowsCount());
    super.ngOnInit();
  }

  public ngOnDestroy() {
    this.selection$ && this.selection$.unsubscribe();
    this.removeHoverStateListener();
    super.ngOnDestroy();
  }

  public rebind() {
    // the method is necessary for the correct pagination
    this.grid.loading = false;
  }
}
